import React, { DragEvent, useEffect, useState } from 'react';
import { SimulationMovableInteraction } from '../../../types/v2';


interface ObjectTargetControlsProps {
  interaction: SimulationMovableInteraction;
  currentValue: unknown;
  disabled?: boolean;
  className?: string;
  onAction: (actionId: string) => void;
  onSelectionChange?: (selected: boolean) => void;
}


export const ObjectTargetControls: React.FC<ObjectTargetControlsProps> = ({
  interaction,
  currentValue,
  disabled = false,
  className = '',
  onAction,
  onSelectionChange,
}) => {
  const [selected, setSelected] = useState(false);
  const [dragTargetId, setDragTargetId] = useState<string | null>(null);

  useEffect(() => {
    if (disabled && selected) {
      setSelected(false);
      setDragTargetId(null);
      onSelectionChange?.(false);
    }
  }, [disabled, onSelectionChange, selected]);

  const setSelection = (nextSelected: boolean) => {
    setSelected(nextSelected);
    onSelectionChange?.(nextSelected);
  };
  const submitTarget = (actionId: string) => {
    if (disabled) return;
    setSelection(false);
    onAction(actionId);
  };
  const startDrag = (event: DragEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    setSelection(true);
    event.dataTransfer?.setData('application/x-elevate-simulation-object', interaction.id);
    event.dataTransfer?.setData('text/plain', interaction.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  };
  const acceptDrop = (
    event: DragEvent<HTMLButtonElement>,
    actionId: string,
  ) => {
    const sourceId = typeof event.dataTransfer?.getData === 'function'
      ? (
        event.dataTransfer.getData('application/x-elevate-simulation-object')
        || event.dataTransfer.getData('text/plain')
      )
      : interaction.id;
    if (sourceId && sourceId !== interaction.id) return;
    event.preventDefault();
    setDragTargetId(null);
    submitTarget(actionId);
  };

  return (
    <div className={className}>
      <button
        type="button"
        className="simulation-movable-object"
        onClick={() => setSelection(!selected)}
        draggable={!disabled}
        onDragStart={startDrag}
        disabled={disabled}
        aria-expanded={selected}
        aria-controls={`simulation-targets-${interaction.id}`}
        aria-label={`${interaction.label}. Drag or activate to choose a destination.`}
      >
        {interaction.label}
      </button>

      {selected ? (
        <div
          id={`simulation-targets-${interaction.id}`}
          className="simulation-target-picker"
          role="group"
          aria-label={`Choose a destination for ${interaction.label}`}
        >
          <p>Choose a destination for {interaction.label}.</p>
          <div>
            {interaction.targets.map((target) => (
              <button
                key={target.id}
                type="button"
                className={[
                  'simulation-object-target',
                  `simulation-object-target-${target.id}`,
                  currentValue === target.state_value ? 'is-current' : '',
                  dragTargetId === target.id ? 'is-drop-active' : '',
                ].filter(Boolean).join(' ')}
                aria-pressed={currentValue === target.state_value}
                disabled={disabled}
                onClick={() => submitTarget(target.action_id)}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragTargetId(target.id);
                }}
                onDragLeave={() => setDragTargetId(null)}
                onDrop={(event) => acceptDrop(event, target.action_id)}
              >
                {target.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
